<script lang="ts">
  import type { FormEventHandler, KeyboardEventHandler } from 'svelte/elements';
  import { tv } from 'tailwind-variants';

  import type { TeoText } from '@/teoCore/text/TeoText';
  import { Icon } from '@/ui/base';
  import { useViewType } from '@/ui/state/use-text-state.svelte';

  import SmallGhostButton from '../buttons/SmallGhostButton.svelte';

  let {
    title,
    selected = false,
    onDuplicate,
    onClose,
    onclick,
    onChangeTitle,
    teoText,
  }: {
    title: string;
    selected?: boolean;
    onDuplicate?: () => void;
    onClose?: () => void;
    onclick?: () => void;
    onChangeTitle?: (title: string) => void;
    teoText: TeoText;
  } = $props();

  let tabText = $state(title);
  let viewType = useViewType(teoText);
  let type = $derived.by(() => {
    switch ($viewType) {
      case 'new':
        return 'new' as const;
      case 'text-generator':
        return 'generate' as const;
      default:
        return 'analyze' as const;
    }
  });

  const styles = tv({
    base: 'group fr-csb max-w-[216px] flex-1 gap-2 rounded-lg px-4 py-2 font-sans text-label-sm font-medium',
    variants: {
      selected: {
        true: 'min-w-[232px] bg-gradient-to-b  shadow-tab',
        false: 'min-w-[64px] bg-tabs hover:min-w-[216px] hocus:bg-tabs-H',
      },
      viewType: {
        generate:
          'from-gentabs-from/50 to-gentabs-to/50 hocus:from-gentabs-from/80 hocus:to-gentabs-to/80',
        analyze: 'from-tabs-from/50 to-tabs-to/50 hocus:from-tabs-from/80 hocus:to-tabs-to/80',
      },
    },
  });

  const cn = $derived(
    styles({
      selected,
      viewType: type === 'new' ? undefined : type,
    }),
  );
  let inputRef: HTMLInputElement | null = $state(null);

  const resetTitle = () => {
    const oldText = tabText;
    tabText = oldText + ' ';
    setTimeout(() => {
      tabText = oldText;
    });
  };

  const blurInput = () => {
    if (inputRef) {
      inputRef.blur();
    }
  };

  const handleTitleSubmit: FormEventHandler<HTMLInputElement> = (e) => {
    const text = (e.currentTarget.value ?? '').trim();

    if (text.length > 0) {
      tabText = text;
      onChangeTitle?.(text);
    } else {
      resetTitle();
    }
  };

  const handleSpecialKey: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      handleTitleSubmit(e);
      blurInput();
    }
    if (e.key === 'Escape') {
      resetTitle();
      setTimeout(() => {
        blurInput();
      }, 10);
    }
  };

  const handleDuplicate = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDuplicate?.();
  };
</script>

<button class={cn} {onclick}>
  {#if type !== 'new'}
    <div class="w-16 h-16">
      <Icon icon={type === 'generate' ? 'AutoAwesome' : 'TachoSymbol'} size={4} />
    </div>
  {/if}
  {#if selected}
    <input
      title={tabText}
      bind:this={inputRef}
      class="w-full bg-transparent"
      type="text"
      value={tabText}
      onblur={handleTitleSubmit}
      onkeypress={handleSpecialKey}
    />
  {:else}
    <div title={tabText} class="w-full truncate py-1 text-left">{tabText}</div>
  {/if}
  <div class={selected ? 'fr-cc -mr-2' : '-mr-2 hidden group-hover:fr-cc'}>
    {#if type === 'analyze'}
      <SmallGhostButton icon="ContentCopy" label="Duplizieren" onclick={handleDuplicate} />
    {/if}
    <SmallGhostButton icon="Close" label="Schließen" onclick={onClose} />
  </div>
</button>
